import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Trash2, ChevronLeft, ChevronRight, Sun } from 'lucide-react';

import { getHolidayCalendar, getHolidays, addHolidays, deleteHoliday } from '../../services/holidayCalendarService';
import { formatDate } from '../../utils/Utils';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import PolicyRulesPanel from '../../components/ui/PolicyRulesPanel';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const MONTH_NAMES = {
  tr: ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'],
  en: ['January','February','March','April','May','June','July','August','September','October','November','December'],
  ru: ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'],
  kk: ['Қаңтар','Ақпан','Наурыз','Сәуір','Мамыр','Маусым','Шілде','Тамыз','Қыркүйек','Қазан','Қараша','Желтоқсан'],
};
const DAY_HEADERS = {
  tr: ['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'],
  en: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'],
  ru: ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'],
  kk: ['Дс','Сс','Ср','Бс','Жм','Сн','Жс'],
};

function buildCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const pad = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = pad - 1; i >= 0; i--) days.push({ date: new Date(year, month, -i), cur: false });
  for (let d = 1; d <= last.getDate(); d++) days.push({ date: new Date(year, month, d), cur: true });
  let n = 1;
  while (days.length % 7 !== 0) days.push({ date: new Date(year, month + 1, n++), cur: false });
  return days;
}

function dk(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }

// Parse an ISO string into a local-midnight Date representing the intended
// calendar day. Existing holidays in the DB are stored as UTC instants where
// the original author's local date is implicit (e.g. 29 Oct Turkey is stored
// as 2026-10-28T21:00:00Z). Reading the UTC date substring would yield the
// wrong day; we must let the Date object convert to local and then read the
// local calendar components.
function parseLocalDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Serialize a local Date as ISO at UTC midnight using its LOCAL Y-M-D components,
// so the backend stores the same calendar date the user picked regardless of timezone.
function toDateOnlyIso(d) {
  return `${dk(d)}T00:00:00.000Z`;
}

export default function HolidayCalendarDetailsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'tr';
  const { id } = useParams();
  const { canWrite, isSuperAdmin } = useAuth();
  // Atama kuralları (AKM POC §6.2): tatil takvimi kuralı EM modülündedir (PolicyRuleKinds.ModuleFor).
  const canManageRules = canWrite('EM');
  const [activeTab, setActiveTab] = useState('holidays');
  const [loadFailed, setLoadFailed] = useState(false);

  const [calendar, setCalendar] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [delState, setDelState] = useState({ isOpen: false, holidayId: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  // api.js GET'te reddetmez: hata sinyali res.data.status (try/catch ölü kod olurdu). Yalnız İLK yükleme
  // sayfayı boş tutar; ekleme/silme sonrası yenileme içeriği söküp yeniden kurmaz.
  const fetchData = useCallback(async () => {
    const [c, h] = await Promise.all([getHolidayCalendar(id), getHolidays(id)]);
    const ok = c?.data?.status === true && h?.data?.status === true;
    setLoadFailed(!ok);
    setCalendar(c?.data?.status === true ? c.data.data : null);
    const hd = h?.data?.status === true ? h.data.data : [];
    setHolidays(Array.isArray(hd) ? hd : (hd?.data || []));
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const holidayMap = useMemo(() => {
    const m = {};
    holidays.forEach(h => {
      const local = parseLocalDate(h.holidayDate);
      if (local) m[dk(local)] = h;
    });
    return m;
  }, [holidays]);

  const calDays = useMemo(() => buildCalendarDays(year, month), [year, month]);

  const isInRange = (date) => {
    if (!selStart) return false;
    if (!selEnd) return dk(date) === dk(selStart);
    const t = date.getTime(), s = Math.min(selStart.getTime(), selEnd.getTime()), e = Math.max(selStart.getTime(), selEnd.getTime());
    return t >= s && t <= e;
  };

  const handleDayClick = (date) => {
    if (!selStart || selEnd) {
      setSelStart(date);
      setSelEnd(null);
    } else if (date < selStart) {
      setSelEnd(selStart);
      setSelStart(date);
    } else {
      setSelEnd(date);
    }
  };

  const selLabel = useMemo(() => {
    if (!selStart) return '';
    const f = (d) => formatDate(toDateOnlyIso(d), lang);
    return selEnd ? `${f(selStart)} — ${f(selEnd)}` : f(selStart);
  }, [selStart, selEnd, lang]);

  // Hazır (küresel) şablonu yalnız superAdmin, şirket takvimini EM.Write değiştirir (backend WritableAsync);
  // diğerlerinde tatil ekle/sil yok, salt okunur ipucu görünür.
  const readOnly = !calendar || (calendar.isGlobal ? !isSuperAdmin : !canWrite('EM'));

  // Tatil ekleme: veri girişi GenericFormDrawer'da (modal değil). Seçili aralık bilgisi alanın üstünde.
  const addSchema = useMemo(() => [
    { name: 'range', type: 'notice', content: `${t('holidayCalendar.selectedRange')}: ${selLabel}` },
    { name: 'name', label: t('holidayCalendar.holidayName'), type: 'text', grid: 12, required: true },
    { name: 'isHalfDay', label: t('holidayCalendar.isHalfDay'), type: 'checkbox', grid: 12 },
  ], [t, selLabel]);

  const handleAddSave = useCallback(async (formData) => {
    const name = (formData?.name || '').trim();
    if (!name || !selStart) return false;
    const s = selEnd && selEnd < selStart ? selEnd : selStart;
    const e = selEnd && selEnd > selStart ? selEnd : selStart;
    const list = [];
    const cur = new Date(s);
    while (cur <= e) {
      list.push({ name, holidayDate: toDateOnlyIso(cur), isHalfDay: !!formData?.isHalfDay });
      cur.setDate(cur.getDate() + 1);
    }
    try {
      await addHolidays(id, { holidays: list });
    } catch {
      return false; // interceptor toast'u; çekmece açık kalır
    }
    setSelStart(null); setSelEnd(null);
    await fetchData();
    return true;
  }, [id, selStart, selEnd, fetchData]);

  const handleDelConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteHoliday(delState.holidayId);
    } catch {
      setDeleting(false); // interceptor toast'u; onay penceresi açık kalır
      return;
    }
    await fetchData();
    setDelState({ isOpen: false, holidayId: null, name: '' }); setDeleting(false);
  }, [delState.holidayId, fetchData]);

  const grouped = useMemo(() => {
    const g = {};
    [...holidays].sort((a, b) => parseLocalDate(a.holidayDate) - parseLocalDate(b.holidayDate)).forEach(h => {
      const d = parseLocalDate(h.holidayDate);
      const k = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      if (!g[k]) g[k] = { label: `${(MONTH_NAMES[lang] || MONTH_NAMES.tr)[d.getMonth()]} ${d.getFullYear()}`, items: [] };
      g[k].items.push(h);
    });
    return Object.values(g);
  }, [holidays, lang]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y-1); } else setMonth(m => m-1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y+1); } else setMonth(m => m+1); };

  if (loading) return null;

  const tabs = [
    { key: 'holidays', label: t('holidayCalendar.holidays') },
    ...(canManageRules ? [{ key: 'rules', label: t('holidayCalendar.assignmentRules') }] : []),
  ];

  return (
    <Wrapper>
      <StickyHeader>
        <Breadcrumbs items={[{ label: t('holidayCalendar.title'), path: '/holiday-calendars' }, { label: calendar?.name || '...' }]} />
        {tabs.length > 1 && <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />}
      </StickyHeader>

      {activeTab === 'rules' && canManageRules ? (
        <RulesContent>
          <PolicyRulesPanel
            kind="holidayCalendar"
            policyId={id}
            title={t('holidayCalendar.assignmentRules')}
            canWrite={canManageRules}
          />
        </RulesContent>
      ) : (
      <HolidaysContent>
      {loadFailed && <Note role="alert" data-testid="holiday-calendar-load-failed">{t('holidayCalendar.loadFailed')}</Note>}
      {!loadFailed && readOnly && (
        <Note data-testid="holiday-readonly-hint">{t('holidayCalendar.readOnlyHint')}</Note>
      )}
      <Layout>
        <Left>
          <Nav>
            <NBtn onClick={prevMonth}><ChevronLeft size={18} /></NBtn>
            <MLabel>{(MONTH_NAMES[lang] || MONTH_NAMES.tr)[month]} {year}</MLabel>
            <NBtn onClick={nextMonth}><ChevronRight size={18} /></NBtn>
          </Nav>
          <Grid>
            {(DAY_HEADERS[lang] || DAY_HEADERS.tr).map(d => <DH key={d}>{d}</DH>)}
            {calDays.map((day, i) => {
              const k = dk(day.date); const h = holidayMap[k];
              const inR = isInRange(day.date); const isEp = selStart && (dk(day.date) === dk(selStart) || (selEnd && dk(day.date) === dk(selEnd)));
              return (
                <DC key={i} $out={!day.cur} $inR={inR} $ep={isEp} $hol={!!h} onClick={() => !readOnly && day.cur && handleDayClick(day.date)}>
                  <DN $ep={isEp}>{day.date.getDate()}</DN>
                  {h && <HD $half={h.isHalfDay}>{h.isHalfDay ? '½' : ''}</HD>}
                </DC>
              );
            })}
          </Grid>
          {!readOnly && selStart && <SelInfo>{t('holidayCalendar.selectedRange')}: {selLabel}</SelInfo>}
          {!readOnly && (
            <AddBtn data-testid="holiday-add" onClick={() => setAddOpen(true)} disabled={!selStart}>{t('holidayCalendar.addHoliday')}</AddBtn>
          )}
        </Left>

        <Right>
          <PTitle>{t('holidayCalendar.holidays')}</PTitle>
          {holidays.length === 0 ? <Empty>{t('holidayCalendar.noHolidays')}</Empty> : grouped.map(g => (
            <MGroup key={g.label}>
              <MGTitle>{g.label}</MGTitle>
              {g.items.map(h => {
                const d = parseLocalDate(h.holidayDate);
                const ds = d.toLocaleDateString(lang, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                return (
                  <HRow key={h.id}>
                    <HIcon $half={h.isHalfDay}><Sun size={16} /></HIcon>
                    <HContent><HName>{h.name}</HName><HDate>{ds}</HDate></HContent>
                    {h.isHalfDay && <HBadge>{t('holidayCalendar.halfDay')}</HBadge>}
                    {!readOnly && (
                      <DelBtn data-testid={`holiday-delete-${h.id}`} onClick={() => setDelState({ isOpen: true, holidayId: h.id, name: h.name })}><Trash2 size={14} /></DelBtn>
                    )}
                  </HRow>
                );
              })}
            </MGroup>
          ))}
        </Right>
      </Layout>
      </HolidaysContent>
      )}

      <ConfirmDialog isOpen={delState.isOpen} title={t('holidayCalendar.deleteHolidayTitle')}
        message={t('holidayCalendar.deleteHolidayMessage', { name: delState.name })}
        onConfirm={handleDelConfirm} onCancel={() => setDelState({ isOpen: false, holidayId: null, name: '' })} loading={deleting} />

      <GenericFormDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={t('holidayCalendar.addHoliday')}
        schema={addSchema}
        onSave={handleAddSave}
      />
    </Wrapper>
  );
}

// Tek kaydırma: sayfa kabı sabit yükseklikte; "Tatiller" sekmesinin içeriği kendi kabında kayar, "Atama
// Kuralları" sekmesinde ise kap taşmaz ve yalnız kural tablosunun satırları kayar.
const Wrapper = styled.div`display:flex;flex-direction:column;height:100%;min-height:0;overflow:hidden;`;
const StickyHeader = styled.div`flex-shrink:0;`;
const HolidaysContent = styled.div`flex:1;min-height:0;overflow-y:auto;`;
const RulesContent = styled.div`flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;padding-top:8px;`;
const Layout = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:16px;@media(max-width:900px){grid-template-columns:1fr;}`;
const Left = styled.div`background:#fff;border-radius:6px;padding:24px;box-shadow:0 1px 4px rgba(0,66,66,0.06);`;
// İç kaydırma YOK (eski max-height:600px + overflow-y:auto ikinci bir kaydırıcıydı): tek kaydırma HolidaysContent'te.
const Right = styled.div`background:#fff;border-radius:6px;padding:24px;box-shadow:0 1px 4px rgba(0,66,66,0.06);`;
const Note = styled.div`background:#EEF6FD;border-left:3px solid #0291EF;border-radius:4px;padding:10px 12px;margin-top:12px;font-family:'Nunito Sans',sans-serif;font-size:13px;line-height:1.45;color:${({theme})=>theme.colors.text.primary};`;
const PTitle = styled.h3`font-family:'Nunito Sans',sans-serif;font-size:15px;font-weight:700;color:${({theme})=>theme.colors.text.primary};margin:0 0 16px;`;
const Nav = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;`;
const NBtn = styled.button`display:flex;align-items:center;justify-content:center;width:32px;height:32px;border:none;border-radius:4px;background:none;color:#64748b;cursor:pointer;&:hover{background:#f1f5f9;}`;
const MLabel = styled.span`font-family:'Nunito Sans',sans-serif;font-size:15px;font-weight:700;color:${({theme})=>theme.colors.text.primary};`;
const Grid = styled.div`display:grid;grid-template-columns:repeat(7,1fr);gap:2px;`;
const DH = styled.div`text-align:center;font-family:'Nunito Sans',sans-serif;font-size:11px;font-weight:700;color:#94a3b8;padding:6px 0;`;
const DC = styled.div`text-align:center;padding:8px 4px;border-radius:4px;cursor:${({$out})=>$out?'default':'pointer'};opacity:${({$out})=>$out?0.3:1};background:${({$ep,$inR,$hol})=>$ep?'#0291EF':$inR?'rgba(2,145,239,0.1)':$hol?'#fef2f2':'transparent'};position:relative;transition:background 0.1s;&:hover{background:${({$out,$ep})=>$out?'transparent':$ep?'#0291EF':'rgba(2,145,239,0.06)'};}`;
const DN = styled.span`font-family:'Nunito Sans',sans-serif;font-size:13px;font-weight:600;color:${({$ep})=>$ep?'#fff':'inherit'};`;
const HD = styled.div`margin:2px auto 0;${({$half})=>$half?`font-size:9px;font-weight:700;color:#f97316;background:rgba(249,115,22,0.15);border-radius:2px;padding:0 3px;width:fit-content;`:`width:6px;height:6px;border-radius:50%;background:#ef4444;`}`;
const SelInfo = styled.div`font-family:'Nunito Sans',sans-serif;font-size:12px;color:#64748b;text-align:center;margin-top:12px;`;
const AddBtn = styled.button`display:block;width:100%;margin-top:12px;padding:12px;background:${({theme})=>theme.colors.primary.main};color:#fff;border:none;border-radius:4px;font-family:'Nunito Sans',sans-serif;font-size:14px;font-weight:600;cursor:pointer;&:disabled{opacity:0.4;cursor:not-allowed;}&:hover:not(:disabled){opacity:0.9;}`;
const Empty = styled.p`color:#A0A8B4;font-family:'Nunito Sans',sans-serif;font-size:13px;text-align:center;padding:40px 0;`;
const MGroup = styled.div`margin-bottom:20px;`;
const MGTitle = styled.h4`font-family:'Nunito Sans',sans-serif;font-size:13px;font-weight:700;color:#64748b;margin:0 0 8px;padding-bottom:6px;border-bottom:1px solid #f1f5f9;`;
const HRow = styled.div`display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:6px;margin-bottom:6px;background:#fafbfc;&:hover{background:#f1f5f9;}`;
const HIcon = styled.div`color:${({$half})=>$half?'#f97316':'#eab308'};flex-shrink:0;`;
const HContent = styled.div`flex:1;min-width:0;`;
const HName = styled.div`font-family:'Nunito Sans',sans-serif;font-size:14px;font-weight:600;color:${({theme})=>theme.colors.text.primary};`;
const HDate = styled.div`font-family:'Nunito Sans',sans-serif;font-size:12px;color:#64748b;`;
const HBadge = styled.span`font-family:'Nunito Sans',sans-serif;font-size:11px;font-weight:600;padding:2px 8px;border-radius:4px;background:rgba(249,115,22,0.1);color:#f97316;flex-shrink:0;`;
const DelBtn = styled.button`display:flex;align-items:center;justify-content:center;width:28px;height:28px;border:none;border-radius:4px;background:none;color:#A0A8B4;cursor:pointer;flex-shrink:0;&:hover{background:rgba(229,57,53,0.08);color:#E53935;}`;
